import React, { useState, useEffect } from 'react';
import { UserProgress, View, ModuleContent } from './types';
import { Navbar } from './components/Navbar';
import { Home } from './pages/Home';
import { Learn } from './pages/Learn';
import { ModuleDetail } from './pages/ModuleDetail';
import { Compare } from './pages/Compare';
import { Defend } from './pages/Defend';
import { CaseStudies } from './pages/CaseStudies';
import { Assessment } from './pages/Assessment';
import { TeacherResources } from './pages/TeacherResources';
import { ProgressDashboard } from './pages/ProgressDashboard';
import { Sandbox } from './pages/Sandbox';

const App: React.FC = () => {
  const [view, setView] = useState<View>(View.HOME);
  const [activeModule, setActiveModule] = useState<ModuleContent | null>(null);
  
  // Progress State
  const [progress, setProgress] = useState<UserProgress>(() => {
    const saved = localStorage.getItem('malware-app-progress');
    // Migration: ensure new fields exist if loading old state
    const defaultState = {
      completedModules: [],
      moduleScores: {},
      assessmentScore: null,
      caseStudiesCompleted: [],
      dyslexiaMode: false,
      classModeEnabled: false
    };
    
    if (saved) {
      const parsed = JSON.parse(saved);
      return { ...defaultState, ...parsed };
    }
    return defaultState;
  });

  useEffect(() => {
    localStorage.setItem('malware-app-progress', JSON.stringify(progress));
    
    // Apply Dyslexia Mode
    if (progress.dyslexiaMode) {
      document.body.classList.add('dyslexia-mode');
    } else {
      document.body.classList.remove('dyslexia-mode');
    }
  }, [progress]);

  const toggleDyslexia = () => {
    setProgress(prev => ({ ...prev, dyslexiaMode: !prev.dyslexiaMode }));
  };
  
  const toggleClassMode = () => {
    setProgress(prev => ({ ...prev, classModeEnabled: !prev.classModeEnabled }));
  };

  const handleModuleComplete = (id: string, score: number) => {
    setProgress(prev => ({
      ...prev,
      completedModules: Array.from(new Set([...prev.completedModules, id])),
      moduleScores: { ...prev.moduleScores, [id]: score }
    }));
  };

  const handleAssessmentComplete = (score: number) => {
    setProgress(prev => ({ ...prev, assessmentScore: score }));
  };

  const navigateToModule = (module: ModuleContent) => {
    setActiveModule(module);
    setView(View.MODULE_DETAIL);
    window.scrollTo(0,0);
  };

  const renderView = () => {
    switch(view) {
      case View.HOME:
        return <Home start={() => setView(View.LEARN)} progress={progress.completedModules.length} />;
      case View.LEARN:
        return <Learn completedModules={progress.completedModules} onSelectModule={navigateToModule} />;
      case View.SANDBOX:
        return <Sandbox />;
      case View.MODULE_DETAIL:
        return activeModule ? (
          <ModuleDetail 
            module={activeModule} 
            onComplete={handleModuleComplete} 
            isCompleted={progress.completedModules.includes(activeModule.id)}
            classModeEnabled={progress.classModeEnabled}
          />
        ) : <Learn completedModules={progress.completedModules} onSelectModule={navigateToModule} />;
      case View.COMPARE:
        return <Compare />;
      case View.DEFEND:
        return <Defend />;
      case View.CASES:
        return <CaseStudies />;
      case View.ASSESSMENT:
        return <Assessment onComplete={handleAssessmentComplete} previousScore={progress.assessmentScore} />;
      case View.TEACHER:
        return <TeacherResources classModeEnabled={progress.classModeEnabled} toggleClassMode={toggleClassMode} />;
      case View.DASHBOARD:
        return <ProgressDashboard progress={progress} navigateToModule={navigateToModule} />;
      default:
        return <Home start={() => setView(View.LEARN)} progress={progress.completedModules.length} />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col font-sans">
      <Navbar 
        currentView={view} 
        setView={setView} 
        dyslexiaMode={progress.dyslexiaMode}
        toggleDyslexia={toggleDyslexia}
      />
      
      <main className="flex-grow bg-slate-50">
        {renderView()}
      </main>
      
      <footer className="bg-cyber-dark text-slate-400 py-6 text-center text-sm print:hidden">
        <p>© 2024 Malware Basics Educational App. For training purposes only.</p>
      </footer>
    </div>
  );
};

export default App;
